/**
 * Hub AuthProvider — manages Supabase session locally and bridges to
 * @pulse-business-ai/auth SharedAuthProvider.
 *
 * @pulse-business-ai/auth AuthProvider is a PURE context distributor:
 *   <AuthProvider value={authState: AuthState}>{children}</AuthProvider>
 * There is NO `client` prop — all session management lives here in hub code.
 *
 * Hub-specific layers:
 *   - VITE_AUTH_BYPASS dev-mode mock session
 *   - syncLauncherAccessCookie (sb-access-token cookie for CF Access)
 *   - useAppStore notification on TOKEN_REFRESHED
 *   - AuthContext re-export for local consumers that import it directly
 *   - SharedAuthProvider bridge so shared components see a typed AuthState
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Session } from "@supabase/supabase-js";
import {
  AuthProvider as SharedAuthProvider,
  type AuthState,
  type OrgTier,
} from "@pulse-business-ai/auth";
import { supabaseAuth } from "./supabase-auth-config";
import { redirectToAuth } from "@/lib/auth-redirect";
import { useAppStore } from "@/core/store/useAppStore";

// -- Types --

export interface AuthContextValue {
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  refreshToken: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

const SharedAuthProviderCompat = SharedAuthProvider as unknown as React.ComponentType<{
  value: AuthState;
  children: React.ReactNode;
}>;

// -- Helpers --

const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === "true";

function extractRoles(session: Session | null): string[] {
  if (!session?.user) return [];
  const appMeta = session.user.app_metadata as Record<string, unknown> | undefined;
  const roles = appMeta?.roles;
  if (Array.isArray(roles)) {
    return roles.filter((r): r is string => typeof r === "string");
  }
  if (!DEV_AUTH_BYPASS) return [];
  // Dev-bypass fallback: also check user_metadata.roles
  const userMeta = session.user.user_metadata as Record<string, unknown> | undefined;
  const fallbackRoles = userMeta?.roles;
  if (Array.isArray(fallbackRoles)) {
    return fallbackRoles.filter((r): r is string => typeof r === "string");
  }
  return [];
}

/**
 * Derive OrgTier from the role list.
 * Looks for a role matching `tier_<TIER>` (e.g. "tier_T3" → "T3").
 * Defaults to "EXT" if none found.
 */
function extractTier(roles: readonly string[]): OrgTier {
  const VALID_TIERS = new Set<OrgTier>([
    "SYS", "T1", "T2", "T3", "T4", "T5", "FO", "EXT",
  ]);
  for (const role of roles) {
    if (role.startsWith("tier_")) {
      const candidate = role.slice("tier_".length) as OrgTier;
      if (VALID_TIERS.has(candidate)) return candidate;
    }
  }
  return "EXT";
}

function syncLauncherAccessCookie(session: Session | null) {
  if (typeof document === "undefined") return;
  if (!session?.access_token) {
    document.cookie = "sb-access-token=; Path=/; Max-Age=0; SameSite=Lax";
    return;
  }
  const maxAge = Math.max(0, (session.expires_at ?? 0) - Math.floor(Date.now() / 1000));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `sb-access-token=${encodeURIComponent(session.access_token)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function isMissingSessionError(error: unknown): boolean {
  if (!error) return false;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" && "message" in error
        ? String((error as { message?: unknown }).message)
        : String(error);
  return /auth session missing|session.*missing|no active session/i.test(message);
}

// -- HubAuthBridge --
// Manages the Supabase session, exposes the local AuthContext, and bridges to
// the shared AuthProvider via its `value` prop (the only prop it accepts).

function HubAuthBridge({ children }: { children: React.ReactNode }) {
  const sessionRef = useRef<Session | null>(null);
  const [localSession, setLocalSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Bootstrap: restore existing session on mount
  useEffect(() => {
    let cancelled = false;

    supabaseAuth.auth
      .getSession()
      .then(({ data, error }: any) => {
        if (cancelled) return;
        if (error && !isMissingSessionError(error)) {
          throw error;
        }
        const s = data.session ?? null;
        sessionRef.current = s;
        syncLauncherAccessCookie(s);
        setLocalSession(s);
        setIsLoading(false);
      })
      .catch((error: any) => {
        if (cancelled) return;
        sessionRef.current = null;
        syncLauncherAccessCookie(null);
        setLocalSession(null);
        setIsLoading(false);
        if (!isMissingSessionError(error)) {
          console.error("[Auth] Failed to restore session:", error);
        }
      });

    const { data: sub } = supabaseAuth.auth.onAuthStateChange((event: any, session: any) => {
      const s = session ?? null;
      sessionRef.current = s;
      syncLauncherAccessCookie(s);
      setLocalSession(s);
      if (isLoading) setIsLoading(false);

      if (event === "TOKEN_REFRESHED") {
        useAppStore.getState().addNotification({ type: "success", title: "Session refreshed" });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(() => {
    // H3/H4/H5/H8: delegate to the shared auth-redirect utility.
    const current = window.location.pathname + window.location.search + window.location.hash;
    const safe =
      current.startsWith("/") && !current.startsWith("/callback")
        ? current
        : "/";
    redirectToAuth(safe);
  }, []);

  const logout = useCallback(async () => {
    await supabaseAuth.auth.signOut();
  }, []);

  const getAccessToken = useCallback((): string | null => {
    return sessionRef.current?.access_token ?? null;
  }, []);

  const refreshToken = useCallback(async (): Promise<void> => {
    const { data, error } = await supabaseAuth.auth.refreshSession();
    if (error) throw error;
    syncLauncherAccessCookie(data.session);
    sessionRef.current = data.session;
    setLocalSession(data.session);
  }, []);

  const session = localSession;
  const rolesKey = useMemo(
    () => extractRoles(session).sort().join(","),
    [session],
  );
  const roles = useMemo(() => rolesKey.split(",").filter(Boolean), [rolesKey]);

  const isAuthenticated = useMemo(() => {
    if (!session) return false;
    const expiresAt = session.expires_at ?? 0;
    return expiresAt > Math.floor(Date.now() / 1000);
  }, [session]);

  const localValue = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated,
      isLoading,
      roles,
      login,
      logout,
      getAccessToken,
      refreshToken,
    }),
    [session, isAuthenticated, isLoading, roles, login, logout, getAccessToken, refreshToken],
  );

  // Bridge to shared AuthProvider: compute AuthState from local session data.
  // SharedAuthProvider only accepts { value: AuthState } — no client prop.
  const sharedAuthState = useMemo<AuthState>(
    () => ({
      user: session?.user ?? null,
      roles: roles as readonly string[],
      tier: extractTier(roles),
    }),
    [session, roles],
  );

  return (
    <AuthContext.Provider value={localValue}>
      <SharedAuthProviderCompat value={sharedAuthState}>
        {children}
      </SharedAuthProviderCompat>
    </AuthContext.Provider>
  );
}

// -- Dev-bypass path (no Supabase session needed) --

function DevBypassProvider({ children }: { children: React.ReactNode }) {
  const mockSession = useMemo(
    () =>
      ({
        access_token: "mock-token-bypass",
        refresh_token: "mock-refresh-bypass",
        token_type: "bearer",
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        expires_in: 3600,
        user: {
          id: "mock-user-123",
          email: "admin@pulse.local",
          app_metadata: { roles: ["admin", "tier_sys", "dept_ops"] },
          user_metadata: { name: "Dev Admin", preferred_username: "admin" },
          aud: "authenticated",
          created_at: new Date().toISOString(),
        },
      }) as unknown as Session,
    [],
  );

  const login = useCallback(() => {}, []);
  const logout = useCallback(async () => {}, []);
  const getAccessToken = useCallback(() => "mock-token-bypass", []);
  const refreshToken = useCallback(async () => {}, []);

  const mockRoles = ["admin", "tier_sys", "dept_ops"];

  const localValue = useMemo<AuthContextValue>(
    () => ({
      session: mockSession,
      isAuthenticated: true,
      isLoading: false,
      roles: mockRoles,
      login,
      logout,
      getAccessToken,
      refreshToken,
    }),
    [mockSession, login, logout, getAccessToken, refreshToken],
  );

  const sharedAuthState = useMemo<AuthState>(
    () => ({
      user: mockSession.user as unknown as import("@supabase/supabase-js").User,
      roles: mockRoles as readonly string[],
      tier: "SYS", // tier_sys → SYS in dev bypass
    }),
    [mockSession],
  );

  return (
    <AuthContext.Provider value={localValue}>
      <SharedAuthProviderCompat value={sharedAuthState}>
        {children}
      </SharedAuthProviderCompat>
    </AuthContext.Provider>
  );
}

// -- Public AuthProvider --

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      console.warn("[Auth] Dev mode: Injecting mock session (VITE_AUTH_BYPASS=true)");
    }
  }, []);

  if (DEV_AUTH_BYPASS) {
    return <DevBypassProvider>{children}</DevBypassProvider>;
  }

  return <HubAuthBridge>{children}</HubAuthBridge>;
}
